import { EventLogData } from '@moderepo/mode-apis';


/**
 * These are the device statuses used by the backend/device. There might be other statuses that backend can return but these are the only one
 * the UI need to know about.
 */
export enum ActlyzerDeviceStatus {
    ERROR = 'ERROR',

    // The device is in idle waiting for streaming request. Note that this does not mean the device is streaming, it just mean the
    // device is ready for requests
    READY = 'READY',

    // The device is starting to stream. This status means someone sent a request to start streaming and the device is preparing. At this
    // stage, the device is still not streaming yet
    STARTING = 'STARTING',

    // The device is streaming.
    RUNNING = 'RUNNING',

    RESETTING = 'RESETTING'
}


/**
 * These are the camera streaming state create and used by the front end.
 */
export enum ActlyzerCameraStreamState {
    // The player IS NOT streaming
    NOT_STREAMING = 'not_streaming',

    // The player dispatched an event to start streaming but the server is not ready yet
    START_STREAMING_REQUESTED = 'start_streaming_requested',

    // The server is ready to stream but has not provide any data yet
    WAITING_FOR_VIDEO = 'waiting_for_video',

    // The player is triggered to stop but the server has not stopped streaming yet
    STOP_STREAMING_REQUESTED = 'stop_streaming_requested',

    // The player IS streaming
    STREAMING = 'streaming',

    // The player is on PAUSE. This status is not the same as NOT_PLAYING. The video stream might still be sending video frames but the
    // component just stop showing them.
    ON_PAUSE = 'on_pause',
}


export interface ActlyzerVideoInfo {
    readonly CamID: number;
    readonly ThumbnailURL: string;
    readonly VideoURL: string;
}


export interface ActlyzerEventPayload {
    readonly ActlyzerID: string;
    readonly CamID: string;
    readonly EndTime: string;
    readonly EventID: string;
    readonly EventKey: string;
    readonly OutputCamera: readonly number[];
    readonly RuleID: string;
    readonly StartTime: string;
    readonly TransferMode: number;
    readonly TriggerEndTime: string;
    readonly TriggerStartTime: string;
}


export interface ActlyzerEvent {
    readonly HeaderType: [string, number];
    readonly Payload: ActlyzerEventPayload;
    readonly PayloadType: [string, number];
    readonly SequenceNumber: number;
    readonly Videos?: readonly ActlyzerVideoInfo[] | undefined;
}


/**
 * This is the data structure of the the event log data returned by the Actlyzer custom application. It is an EventLogData except instead of
 * having an array of "records", it contains an array of "act_event_list"
 */
export interface ActlyzerVideoEventLogData extends Omit<EventLogData, 'records'> {
    readonly act_event_list?: readonly ActlyzerEvent[] | undefined;
}


/**
 * Once we fetched actlyzer's event log data, we will extract all the data we need and create these ActlyzerEventVideoInfo objects. These are
 * the only info from the event data that we need to be able to display in the UI.
 */
export interface ActlyzerEventVideoInfo {
    readonly eventKey: string;
    readonly startTime: string;
    readonly endTime: string;
    readonly cameraId?: number | undefined;
    readonly thumbnail?: string | undefined;
    readonly videoUrl?: string | undefined;
}


export interface VideoFrame {
    readonly image: string;
    readonly timestamp: number;

    // The number of people detected in the video frame image
    readonly headCount?: number | undefined;
}



/**
 * Interface for storing all the information about a camera
 */
export interface ActlyzerCameraInfo {
    readonly cameraId: number;
    readonly streamingState: ActlyzerCameraStreamState;

    // temporary image of the camera to display when the camera is not playing
    readonly thumbnail?: string | undefined;

    readonly currentVideoFrame?: VideoFrame | undefined;
}



// The default event data name to be used for creating the searchKeys.
export const DEFAULT_EVENT_DATA_NAME = 'act_event_data';

// The default app proxy resource path where event data can be fetched
export const DEFAULT_EVENT_RESOURCE_PATH = '/actlyzer/events';


/**
 * Get user friendly display name for a streaming state
 */
export const getStreamingStateDisplayName = (state: ActlyzerCameraStreamState): string => {
    const names = Object.freeze({
        [ActlyzerCameraStreamState.NOT_STREAMING]            : 'video_streaming.state.not_streaming',
        [ActlyzerCameraStreamState.START_STREAMING_REQUESTED]: 'video_streaming.state.start_streaming_requested',
        [ActlyzerCameraStreamState.WAITING_FOR_VIDEO]        : 'video_streaming.state.waiting_for_video',
        [ActlyzerCameraStreamState.STOP_STREAMING_REQUESTED] : 'video_streaming.state.stop_streaming_requested',
        [ActlyzerCameraStreamState.ON_PAUSE]                 : 'video_streaming.state.on_pause',
        [ActlyzerCameraStreamState.STREAMING]                : 'video_streaming.state.streaming',
    });

    return names[state];
};
